/**
 * useCosmetics — TanStack Query + mutation hooks over the Wave 2 avatar-shop API.
 *
 * Endpoints (mounted at /api/cosmetics by apps/api/src/app.ts):
 *   GET  /api/cosmetics?for_member=<id>   catalogue annotated for the member
 *   GET  /api/cosmetics/owned             caller's wardrobe (equipped state)
 *   GET  /api/cosmetics/admin             full active catalogue (admin scope)
 *   POST /api/cosmetics/:id/buy           INSTANT purchase (no approval)
 *   POST /api/cosmetics/:id/equip         toggle equipped {equipped}
 *   POST /api/cosmetics                   create (admin scope)
 *   PATCH/DELETE /api/cosmetics/:id       update / archive (admin scope)
 *
 * The buy mutation surfaces the 422 details ('insufficient_points' with
 * balance/cost, or 'age_locked' with min_age) on `err.body` so the consumer
 * can render inline copy. Pattern mirrors useRewardsQueries: direct
 * globalThis.fetch with a typed FetchError, no axios.
 *
 * On a successful buy we invalidate the catalogue, the wardrobe and the
 * caller's balance so the spent points reflect immediately (the cosmetic.*
 * WS broadcast also bumps the wallet via usePointsWS — this invalidate is the
 * belt-and-braces for the shop's own surfaces).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CosmeticKind } from '@stell/shared';

// ─── Row shapes (mirror the server DTOs) ─────────────────────────────────────
export interface CosmeticItemRow {
  id: string;
  kind: CosmeticKind;
  name: string;
  description: string | null;
  asset_key: string;
  point_cost: number;
  min_age: number;
  active: number;
  created_by: string;
  created_at: string;
}

export interface CosmeticCatalogRow extends CosmeticItemRow {
  owned: boolean;
  equipped: boolean;
  locked_age: boolean;
}

export interface OwnedCosmeticRow extends CosmeticItemRow {
  equipped: boolean;
  point_cost_at_purchase: number;
  purchased_at: string;
}

interface FetchError extends Error {
  status: number;
  body?: unknown;
}

async function fetchJson<T>(url: string): Promise<T> {
  const r = await globalThis.fetch(url, { credentials: 'same-origin' });
  if (!r.ok) {
    let parsed: unknown;
    try {
      parsed = await r.json();
    } catch {
      parsed = undefined;
    }
    const e = new Error(`fetch_failed:${r.status}`) as FetchError;
    e.status = r.status;
    e.body = parsed;
    throw e;
  }
  return (await r.json()) as T;
}

async function sendJson<T>(
  method: 'POST' | 'PATCH' | 'DELETE',
  url: string,
  body?: unknown,
): Promise<T> {
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: { 'content-type': 'application/json' },
  };
  if (body !== undefined) init.body = JSON.stringify(body);
  const r = await globalThis.fetch(url, init);
  if (!r.ok) {
    let parsed: unknown;
    try {
      parsed = await r.json();
    } catch {
      parsed = undefined;
    }
    const e = new Error(`fetch_failed:${r.status}`) as FetchError;
    e.status = r.status;
    e.body = parsed;
    throw e;
  }
  try {
    return (await r.json()) as T;
  } catch {
    return undefined as T;
  }
}

// ─── Queries ──────────────────────────────────────────────────────────────────

/** Catalogue annotated (owned/equipped/locked_age) for the member. */
export function useCosmeticCatalog(memberId: string | null | undefined) {
  return useQuery<CosmeticCatalogRow[]>({
    queryKey: ['cosmetics', 'catalog', memberId ?? 'anonymous'],
    queryFn: () =>
      fetchJson<CosmeticCatalogRow[]>(
        `/api/cosmetics?for_member=${encodeURIComponent(memberId ?? '')}`,
      ),
    enabled: !!memberId,
    staleTime: 30_000,
  });
}

/** Caller's wardrobe (owned items + equipped state). */
export function useOwnedCosmetics(memberId: string | null | undefined) {
  return useQuery<OwnedCosmeticRow[]>({
    queryKey: ['cosmetics', 'owned', memberId ?? 'anonymous'],
    queryFn: () =>
      fetchJson<OwnedCosmeticRow[]>(
        `/api/cosmetics/owned?for_member=${encodeURIComponent(memberId ?? '')}`,
      ),
    enabled: !!memberId,
    staleTime: 30_000,
  });
}

/** Full active catalogue (admin editor). */
export function useCosmeticsAdmin(enabled = true) {
  return useQuery<CosmeticItemRow[]>({
    queryKey: ['cosmetics', 'admin'],
    queryFn: () => fetchJson<CosmeticItemRow[]>('/api/cosmetics/admin'),
    enabled,
    staleTime: 30_000,
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

/**
 * Kid taps "Kjøp" → POST /api/cosmetics/:id/buy. The purchase is terminal the
 * instant it resolves (no approval). On 422 the FetchError carries
 * `body.error` ('insufficient_points' | 'age_locked' | 'already_owned') plus
 * `body.balance/cost/min_age`; the consumer renders inline copy + a celebration
 * on success.
 */
export function useBuyCosmetic(viewerId: string) {
  const qc = useQueryClient();
  return useMutation<CosmeticItemRow, FetchError, string>({
    mutationFn: (itemId) =>
      sendJson<CosmeticItemRow>('POST', `/api/cosmetics/${itemId}/buy`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'catalog', viewerId] });
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'owned', viewerId] });
      void qc.invalidateQueries({ queryKey: ['points', 'balance', viewerId] });
    },
  });
}

/** Toggle equipped → POST /api/cosmetics/:id/equip {equipped}. */
export function useEquipCosmetic(viewerId: string) {
  const qc = useQueryClient();
  return useMutation<
    OwnedCosmeticRow,
    FetchError,
    { itemId: string; equipped: boolean }
  >({
    mutationFn: ({ itemId, equipped }) =>
      sendJson<OwnedCosmeticRow>('POST', `/api/cosmetics/${itemId}/equip`, { equipped }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'catalog', viewerId] });
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'owned', viewerId] });
    },
  });
}

// ─── Admin mutations ──────────────────────────────────────────────────────────
export interface CreateCosmeticInput {
  kind: CosmeticKind;
  name: string;
  description?: string;
  asset_key: string;
  point_cost: number;
  min_age?: number;
}

export function useCreateCosmetic() {
  const qc = useQueryClient();
  return useMutation<CosmeticItemRow, FetchError, CreateCosmeticInput>({
    mutationFn: (input) => sendJson<CosmeticItemRow>('POST', '/api/cosmetics', input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'admin'] });
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'catalog'] });
    },
  });
}

export function useUpdateCosmetic() {
  const qc = useQueryClient();
  return useMutation<
    CosmeticItemRow,
    FetchError,
    { id: string; patch: Partial<CreateCosmeticInput> }
  >({
    mutationFn: ({ id, patch }) =>
      sendJson<CosmeticItemRow>('PATCH', `/api/cosmetics/${id}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'admin'] });
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'catalog'] });
    },
  });
}

export function useArchiveCosmetic() {
  const qc = useQueryClient();
  return useMutation<CosmeticItemRow, FetchError, string>({
    mutationFn: (id) => sendJson<CosmeticItemRow>('DELETE', `/api/cosmetics/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'admin'] });
      void qc.invalidateQueries({ queryKey: ['cosmetics', 'catalog'] });
    },
  });
}

export type { FetchError };
